import React, { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Briefcase, Mail, Save, Pencil, X } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { useToast } from "../../hooks/use-toast";

const TABS = [
  { key: "vacancies", label: "Vacancies", icon: Briefcase },
  { key: "emails", label: "Applicant Emails", icon: Mail },
];

const emptyVacancy = () => ({ title: "", location: "", type: "", description: "", active: true, order: 0 });

function VacancyForm({ initial, onSave, onCancel, saving }) {
  const [v, setV] = useState(initial || emptyVacancy());
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4" data-testid="vacancy-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-navy font-semibold">Job title *</Label>
          <Input data-testid="vacancy-title" value={v.title} onChange={set("title")} placeholder="e.g. Client Manager" className="mt-1.5" />
        </div>
        <div>
          <Label className="text-navy font-semibold">Location</Label>
          <Input data-testid="vacancy-location" value={v.location} onChange={set("location")} placeholder="e.g. Tamworth / Hybrid" className="mt-1.5" />
        </div>
        <div>
          <Label className="text-navy font-semibold">Type</Label>
          <Input data-testid="vacancy-type" value={v.type} onChange={set("type")} placeholder="e.g. Full-time" className="mt-1.5" />
        </div>
        <div>
          <Label className="text-navy font-semibold">Display order</Label>
          <Input data-testid="vacancy-order" type="number" value={v.order} onChange={(e) => setV((s) => ({ ...s, order: Number(e.target.value) }))} className="mt-1.5" />
        </div>
      </div>
      <div>
        <Label className="text-navy font-semibold">Description</Label>
        <Textarea data-testid="vacancy-description" value={v.description} onChange={set("description")} rows={5} placeholder="What the role involves, who you're looking for, and any key requirements." className="mt-1.5" />
      </div>
      <div className="flex items-center gap-3">
        <Switch data-testid="vacancy-active" checked={v.active} onCheckedChange={(c) => setV((s) => ({ ...s, active: c }))} />
        <span className="text-sm text-slate-600">{v.active ? "Live on the Careers page" : "Hidden (draft)"}</span>
      </div>
      <div className="flex gap-3">
        <Button data-testid="vacancy-save" onClick={() => onSave(v)} disabled={saving || !v.title.trim()} className="bg-orange hover:bg-orange-dark text-white rounded-full px-6 font-bold">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={15} className="mr-1.5" /> Save role</>}
        </Button>
        <Button variant="ghost" onClick={onCancel} className="rounded-full">Cancel</Button>
      </div>
    </div>
  );
}

function VacanciesPanel() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | vacancy object
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.vacancies.list().then((d) => { setItems(d); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async (v) => {
    setSaving(true);
    try {
      if (editing === "new") { await adminApi.vacancies.create(v); toast({ title: "Vacancy added" }); }
      else { await adminApi.vacancies.update(editing.id, v); toast({ title: "Vacancy updated" }); }
      setEditing(null); load();
    } catch { toast({ title: "Save failed", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    setItems(items.filter((i) => i.id !== id));
    try { await adminApi.vacancies.remove(id); toast({ title: "Vacancy removed" }); } catch { load(); }
  };

  const toggleActive = async (v) => {
    const active = !v.active;
    setItems(items.map((i) => (i.id === v.id ? { ...i, active } : i)));
    try { await adminApi.vacancies.update(v.id, { ...v, active }); } catch { load(); }
  };

  if (loading) return <div className="text-slate-400 p-6">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{items.length} role(s). Active roles show on the public Careers page with an "Apply for this role" button.</p>
        {!editing && <Button data-testid="add-vacancy-btn" onClick={() => setEditing("new")} className="bg-orange hover:bg-orange-dark text-white rounded-full px-5 font-bold"><Plus size={16} className="mr-1" /> Add role</Button>}
      </div>

      {editing && <VacancyForm initial={editing === "new" ? null : editing} onSave={save} onCancel={() => setEditing(null)} saving={saving} />}

      {items.length === 0 && !editing ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400"><Briefcase size={34} className="mx-auto mb-3 text-slate-300" />No vacancies yet. Add one to advertise a role.</div>
      ) : (
        <div className="space-y-3">
          {items.map((v) => (
            <div key={v.id} data-testid={`vacancy-row-${v.id}`} className={`rounded-xl border p-4 flex items-center gap-4 ${v.active ? "bg-white border-slate-100" : "bg-slate-50/70 border-slate-100"}`}>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-navy truncate">{v.title}</p>
                <p className="text-xs text-slate-400 truncate">{[v.location, v.type].filter(Boolean).join(" · ") || "—"}</p>
              </div>
              <button onClick={() => toggleActive(v)} data-testid={`vacancy-toggle-${v.id}`} className={`text-xs font-bold px-3 py-1 rounded-full border ${v.active ? "bg-green-50 text-green-600 border-green-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{v.active ? "Live" : "Draft"}</button>
              <button onClick={() => setEditing(v)} data-testid={`vacancy-edit-${v.id}`} className="w-9 h-9 rounded-lg bg-navy/5 hover:bg-navy/10 flex items-center justify-center text-navy"><Pencil size={15} /></button>
              <button onClick={() => remove(v.id)} data-testid={`vacancy-delete-${v.id}`} className="w-9 h-9 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center text-red-500"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmailCard({ title, hint, value, onChange, testidPrefix }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
      <div>
        <h3 className="font-bold text-navy">{title}</h3>
        <p className="text-sm text-slate-500 mt-1">{hint}</p>
      </div>
      <div>
        <Label className="text-navy font-semibold">Subject</Label>
        <Input data-testid={`${testidPrefix}-subject`} value={value.subject} onChange={(e) => onChange({ ...value, subject: e.target.value })} className="mt-1.5" />
      </div>
      <div>
        <Label className="text-navy font-semibold">Message</Label>
        <Textarea data-testid={`${testidPrefix}-body`} value={value.body} onChange={(e) => onChange({ ...value, body: e.target.value })} rows={9} className="mt-1.5 font-mono text-sm" />
      </div>
    </div>
  );
}

function EmailsPanel() {
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.careersSettings.get().then(setSettings).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try { const res = await adminApi.careersSettings.update(settings); setSettings(res); toast({ title: "Emails saved" }); }
    catch { toast({ title: "Save failed", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="text-slate-400 p-6">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="bg-orange/5 border border-orange/20 rounded-xl p-4 text-sm text-navy">
        Use <code className="bg-white px-1.5 py-0.5 rounded border border-orange/20 font-semibold">{"{name}"}</code> for the applicant's first name and <code className="bg-white px-1.5 py-0.5 rounded border border-orange/20 font-semibold">{"{role}"}</code> for the role they applied for. These are filled in automatically.
      </div>
      <EmailCard
        title="Auto-acknowledge email"
        hint="Sent automatically the moment someone submits their CV."
        value={settings.ack_email}
        onChange={(ack_email) => setSettings((s) => ({ ...s, ack_email }))}
        testidPrefix="ack"
      />
      <EmailCard
        title="Rejection email"
        hint="Sent when you set a candidate to 'Rejected' and choose to send the email."
        value={settings.rejection_email}
        onChange={(rejection_email) => setSettings((s) => ({ ...s, rejection_email }))}
        testidPrefix="rejection"
      />
      <Button data-testid="save-careers-emails" onClick={save} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full px-6 font-bold">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <><Save size={15} className="mr-1.5" /> Save emails</>}
      </Button>
    </div>
  );
}

export default function CareersManager() {
  const [tab, setTab] = useState("vacancies");
  return (
    <div>
      <h1 className="font-display font-extrabold text-2xl text-navy mb-1">Careers</h1>
      <p className="text-sm text-slate-500 mb-6">Manage open roles and the automatic emails applicants receive.</p>
      <div className="flex gap-2 mb-6">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`careers-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.key ? "bg-navy text-white" : "bg-white border border-slate-200 text-navy hover:border-orange/40"}`}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "vacancies" ? <VacanciesPanel /> : <EmailsPanel />}
    </div>
  );
}
